import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Form, Row, Col, Pagination, Badge, Offcanvas } from 'react-bootstrap';
import { agentService } from '../../services/agentService';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const currencyMap = {
    'None': '', 'USD': '$', 'GBP': '£', 'BTC': '₿', 'EUR': '€', 'CHF': '₣',
    'KRW': '₩', 'TRY': '₺', 'INR': '₹', 'CNY': '¥', 'THB': '฿',
    'RUB': '₽', 'UAH': '₴', 'BDT': '৳', 'JPY': '¥', 'ILS': '₪',
    'AED': 'د.إ', 'SAR': 'ر.س'
};

const StockOrders = () => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState({
        orderNo: '',
        userId: '',
        symbol: '',
        status: 'all'
    });

    const fetchOrders = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const response = await agentService.getTrades({
                page,
                limit: 20,
                ...filters
            });
            if (response.success) {
                setOrders(response.trades);
                setTotalPages(response.pagination.pages);
                setCurrentPage(response.pagination.page);
            }
        } catch (error) {
            toast.error(t('error_fetching_trades'));
        } finally {
            setLoading(false);
        }
    }, [filters, t]);

    useEffect(() => {
        fetchOrders(1);
    }, [fetchOrders]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handlePageChange = (page) => {
        fetchOrders(page);
    };

    const [showFilterDrawer, setShowFilterDrawer] = useState(false);

    // ...existing code...

    const resetFilters = () => {
        setFilters({
            orderNo: '',
            userId: '',
            symbol: '',
            status: 'all'
        });
    };

    const TradeStatusBadge = ({ status, pnl }) => {
        if (status === 'pending') return <Badge bg="warning" text="dark" className="rounded-pill fw-normal">{t('pending')}</Badge>;

        const isWin = status === 'won';
        return (
            <Badge bg={isWin ? 'success' : 'danger'} className={`rounded-pill fw-normal bg-opacity-25 text-${isWin ? 'success' : 'danger'} border border-${isWin ? 'success' : 'danger'} border-opacity-25`}>
                {isWin ? t('profit') : t('loss')}
            </Badge>
        );
    };

    return (
        <div className="majestic-page-wrapper">
            {/* Desktop Filter Bar */}
            <div className="majestic-card filter-bar mb-4">
                <Row className="g-3 align-items-center w-100">
                    <Col xs={12} md="auto">
                        <div className="d-flex align-items-center">
                            <span className="form-label mb-0 text-secondary small me-2" style={{ minWidth: '80px' }}>{t('user_id')}</span>
                            <Form.Control
                                type="text"
                                name="userId"
                                placeholder={t('enter_user_id')}
                                value={filters.userId}
                                onChange={handleFilterChange}
                                className="w-100"
                                style={{ minWidth: '200px' }}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md="auto" className="ms-auto text-end">
                        <div className="d-flex gap-2 justify-content-end">
                            <Button variant="primary" size="sm" className="px-3" onClick={() => fetchOrders(1)} disabled={loading}>
                                {loading ? t('loading') : t('query')}
                            </Button>
                            <Button variant="secondary" size="sm" className="px-3" onClick={resetFilters}>{t('reset')}</Button>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Mobile Filter Trigger */}
            <div className="filter-drawer-btn d-md-none" onClick={() => setShowFilterDrawer(true)}>
                <i className="bi bi-funnel"></i>
            </div>

            {/* Mobile Filter Drawer */}
            <Offcanvas
                show={showFilterDrawer}
                onHide={() => setShowFilterDrawer(false)}
                placement="bottom"
                className="majestic-card border-top border-secondary h-auto rounded-top-4"
                style={{ maxHeight: '85vh', background: 'var(--bg-card)' }}
            >
                <Offcanvas.Header closeButton className="border-bottom border-light border-opacity-10">
                    <Offcanvas.Title className="fw-bold fs-5">{t('filter_options')}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="p-4">
                    <Form className="d-flex flex-column gap-3">
                        <div>
                            <label className="text-secondary small mb-1">{t('user_id')}</label>
                            <Form.Control
                                size="lg"
                                type="text"
                                name="userId"
                                placeholder={t('enter_user_id')}
                                value={filters.userId}
                                onChange={handleFilterChange}
                                className="bg-dark text-white border-secondary"
                            />
                        </div>
                        <div className="d-flex gap-2 mt-3">
                            <Button variant="secondary" size="lg" className="flex-grow-1" onClick={resetFilters}>{t('reset')}</Button>
                            <Button variant="primary" size="lg" className="flex-grow-1 fw-bold" onClick={() => { fetchOrders(1); setShowFilterDrawer(false); }}>{t('query')}</Button>
                        </div>
                    </Form>
                </Offcanvas.Body>
            </Offcanvas>

            {/* Mobile Card List View */}
            <div className="mobile-card-list">
                {orders.length === 0 ? (
                    <div className="text-center py-5 text-secondary">{t('no_data')}</div>
                ) : (
                    orders.map(order => (
                        <div key={order.id} className="majestic-mobile-card">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div className="d-flex align-items-center gap-2">
                                    <span className={`badge ${order.type === 'buy' ? 'bg-success' : 'bg-danger'} bg-opacity-25 ${order.type === 'buy' ? 'text-success' : 'text-danger'} border-0`}>
                                        {order.type === 'buy' ? t('buying') : t('selling')}
                                    </span>
                                    <span className="fw-bold fs-5">{order.pair}</span>
                                </div>
                                <TradeStatusBadge status={order.status} pnl={order.pnl} />
                            </div>

                            <div className="d-flex justify-content-between align-items-center text-secondary extra-small mb-3 border-bottom border-secondary border-opacity-10 pb-2">
                                <span className="info-value">{new Date(order.createdAt).toLocaleString('en-GB', { hour12: false })}</span>
                                <span>ID: {order.orderNo || order.id.slice(0, 8).toUpperCase()}</span>
                            </div>

                            <div className="row g-2">
                                <div className="col-6">
                                    <div className="card-label">{t('account')}</div>
                                    <div className="card-value">{order.User?.username}</div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="card-label">{t('profit_and_loss')}</div>
                                    <div className={`card-value fw-bold ${order.pnl >= 0 ? 'text-success' : 'text-danger'}`}>
                                        {(order.User?.currency && currencyMap[order.User.currency] !== undefined) ? currencyMap[order.User.currency] : (order.User?.currency === 'None' ? '' : '$')}
                                        {(order.pnl || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="card-label">{t('purchase_amount')}</div>
                                    <div className="card-value">
                                        {(order.User?.currency && currencyMap[order.User.currency] !== undefined) ? currencyMap[order.User.currency] : (order.User?.currency === 'None' ? '' : '$')}
                                        {order.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="card-label">{t('periods')}</div>
                                    <div className="card-value">{order.duration}s</div>
                                </div>
                                <div className="col-6">
                                    <div className="card-label">{t('purchase_price')}</div>
                                    <div className="card-value">{order.entryPrice?.toFixed(2)}</div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="card-label">{t('price_at_closing')}</div>
                                    <div className="card-value">{order.exitPrice?.toFixed(2) || '-'}</div>
                                </div>
                                <div className="col-6">
                                    <div className="card-label">{t('leverage_ratio')}</div>
                                    <div className="card-value">x{order.leverage || 1}</div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="card-label">{t('contract_profit')}</div>
                                    <div className="card-value">{order.profitRate || 90}%</div>
                                </div>
                                <div className="col-6">
                                    <div className="card-label">{t('user_categories')}</div>
                                    <div className="card-value small text-secondary">
                                        {order.User?.isSimulated ? t('simulated_account') : t('normal_account')}
                                    </div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="card-label">{t('designated_pnl')}</div>
                                    <div className="card-value small text-secondary">
                                        {order.User?.winLossControl?.isActive ? (order.User.winLossControl.controlLevel === 'high' ? t('win_strategy') : t('deficit_strategy')) : t('with_the_market')}
                                    </div>
                                </div>
                                <div className="col-12 mt-2 pt-2 border-top border-secondary border-opacity-10">
                                    <div className="d-flex justify-content-between extra-small text-secondary">
                                        <span>{t('purchase_timestamp')}: {new Date(order.createdAt).toLocaleString('en-GB', { hour12: false })}</span>
                                    </div>
                                    {order.resolveAt && (
                                        <div className="d-flex justify-content-between extra-small text-secondary">
                                            <span>{t('closing_timestamp')}: {new Date(order.resolveAt).toLocaleString('en-GB', { hour12: false })}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Data Grid */}
            <div className="majestic-card p-0 majestic-table-container">
                <Table hover className="border-0 mb-0 text-nowrap">
                    <thead>
                        <tr>
                            <th>{t('id', 'ID')}</th>
                            <th>{t('account')}</th>
                            <th>{t('order_number')}</th>
                            <th>{t('buying_and_selling')}</th>
                            <th>{t('trade_type')}</th>
                            <th>{t('stock_code')}</th>
                            <th>{t('purchase_amount')}</th>
                            <th>{t('purchase_price')}</th>
                            <th>{t('price_at_closing')}</th>
                            <th>{t('periods')}</th>
                            <th>{t('contract_profit')}</th>
                            <th>{t('leverage_ratio')}</th>
                            <th>{t('user_categories')}</th>
                            <th>{t('purchase_timestamp')}</th>
                            <th>{t('closing_timestamp')}</th>
                            <th>{t('profit_and_loss')}</th>
                            <th>{t('designated_pnl')}</th>
                            <th>{t('win_or_loss')}</th>
                            <th>{t('state')}</th>
                            <th className="text-end">{t('operate')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.map((order, idx) => {
                            const isWin = order.status === 'won';
                            const control = order.User?.winLossControl;
                            let strategyLabel = t('with_the_market');
                            if (control?.isActive) {
                                if (control.controlLevel === 'high' || control.controlLevel === 'medium') strategyLabel = t('win_strategy');
                                else if (control.controlLevel === 'low') strategyLabel = t('deficit_strategy');
                            }

                            return (
                                <tr key={order.id} className="border-bottom">
                                    <td>{idx + 1 + (currentPage - 1) * 10}</td>
                                    <td style={{ color: 'var(--text-primary)' }}>{order.User?.username || 'N/A'}</td>
                                    <td>{order.orderNo || order.id.toUpperCase()}</td>
                                    <td className="px-2" style={{ whiteSpace: 'nowrap' }}>
                                        <span className={order.type === 'buy' ? 'text-success' : 'text-danger'}>
                                            {order.type === 'buy' ? t('buying') : t('selling')}
                                        </span>
                                    </td>
                                    <td className="px-2 text-secondary" style={{ whiteSpace: 'nowrap' }}>{t('with_the_market')}</td>
                                    <td className="px-2 fw-bold" style={{ whiteSpace: 'nowrap' }}>{order.pair}</td>
                                    <td className="px-2" style={{ whiteSpace: 'nowrap' }}>
                                        {(order.User?.currency && currencyMap[order.User.currency] !== undefined) ? currencyMap[order.User.currency] : (order.User?.currency === 'None' ? '' : '$')}
                                        {order.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-2" style={{ whiteSpace: 'nowrap' }}>{order.entryPrice?.toFixed(2)}</td>
                                    <td className="px-2" style={{ whiteSpace: 'nowrap' }}>{order.exitPrice?.toFixed(2) || '-'}</td>
                                    <td className="px-2" style={{ whiteSpace: 'nowrap' }}>{order.duration}</td>
                                    <td className="px-2" style={{ whiteSpace: 'nowrap' }}>{order.profitRate || 90}%</td>
                                    <td className="px-2" style={{ whiteSpace: 'nowrap' }}>{order.leverage || 1}</td>
                                    <td className="px-2 text-secondary" style={{ whiteSpace: 'nowrap' }}>
                                        {order.User?.isSimulated ? t('simulated_account') : t('normal_account')}
                                    </td>
                                    <td className="px-2 text-secondary" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{new Date(order.createdAt).toLocaleString('en-GB', { hour12: false })}</td>
                                    <td className="px-2 text-secondary" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{order.resolveAt ? new Date(order.resolveAt).toLocaleString('en-GB', { hour12: false }) : '-'}</td>
                                    <td className={`px-2 fw-bold ${order.pnl >= 0 ? 'text-success' : 'text-danger'}`} style={{ whiteSpace: 'nowrap' }}>
                                        {(order.User?.currency && currencyMap[order.User.currency] !== undefined) ? currencyMap[order.User.currency] : (order.User?.currency === 'None' ? '' : '$')}
                                        {(order.pnl || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-2 text-secondary" style={{ whiteSpace: 'nowrap' }}>
                                        {strategyLabel}
                                    </td>
                                    <td className="px-2" style={{ whiteSpace: 'nowrap' }}>
                                        {order.status === 'pending' ? '-' : (
                                            <span className={isWin ? 'text-success' : 'text-danger'}>
                                                {isWin ? t('profit') : t('loss')}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-2" style={{ whiteSpace: 'nowrap' }}>
                                        {order.status === 'pending' ? (
                                            <Badge bg="warning" text="dark" className="rounded-pill fw-normal">{t('pending')}</Badge>
                                        ) : (
                                            <span className="text-secondary">{t('finish')}</span>
                                        )}
                                    </td>
                                    <td className="px-2 text-end" style={{ whiteSpace: 'nowrap' }}>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0 text-decoration-none fw-600"
                                            style={{ color: 'var(--primary-blue)' }}
                                        >
                                            {t('details')}
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                        {orders.length === 0 && !loading && (
                            <tr>
                                <td colSpan="19" className="text-center py-5 text-secondary">
                                    {t('no_data')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3 px-2 mb-5 mb-md-0">
                <div className="text-secondary small">
                    {t('showing_results_for')} {orders.length} {t('items')}
                </div>
                <Pagination size="sm" className="mb-0">
                    <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                            <Pagination.Item key={pageNum} active={pageNum === currentPage} onClick={() => handlePageChange(pageNum)}>
                                {pageNum}
                            </Pagination.Item>
                        );
                    })}
                    {totalPages > 5 && <Pagination.Ellipsis disabled />}
                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} />
                    <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || totalPages === 0} />
                </Pagination>
            </div>
        </div>
    );
};

export default StockOrders;
